import { useContext } from 'react';
import { Controller } from 'react-hook-form';

import Select from 'components/partials/select/select';
import FileUploader from 'components/partials/file-uploader/file-uploader';
import Textarea from 'components/partials/textarea/textarea';

import { FileContext } from 'containers/admin/clients/client/tabs-components/files-tab/file/file';

import type { ComponentProps } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';

const FILE_NAMES = [
  'Geographic Analysis',
  'Market Position Analysis',
  'Retention Analysis',
  'Goals & Planning',
];

interface FilesDetailsFormProps {
  customClass?: string;
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  isFormDisabled?: boolean;
}

type SelectFieldValue =
  | string
  | { label: string; value: string }
  | { label: string; value: string }[]
  | undefined;

const FilesDetailsForm = ({
  customClass = '',
  control,
  errors,
  isFormDisabled = false,
}: FilesDetailsFormProps): React.JSX.Element => {
  const { clearErrors } = useContext(FileContext);

  return (
    <div className={`${customClass} flex`}>
      <div className="flex flex-col w-full mr-10 justify-between">
        <Controller
          name="fileName"
          control={control}
          render={({ field }) => {
            const selectProps: ComponentProps<typeof Select> = {
              isCreateble: true,
              fieldError: errors['fileName'],
              label: 'File name',
              labelInTop: true,
              clearInputErr: () => clearErrors && clearErrors('fileName'),
              labelFor: 'fileName',
              errors,
              disabled: isFormDisabled,
              placeholder: 'Start typing to select or use your option...',
              disabledBgColor: '#F2F2F2',
              options: FILE_NAMES.map((name) => ({
                value: name,
                label: name,
              })),
              customClass: '!mt-0 mb-2',
              required: !isFormDisabled,
              'data-testid': 'file-name-select',
              ...field,
              value: field.value as SelectFieldValue,
            };

            return (
              <Select
                {...(selectProps as unknown as ComponentProps<typeof Select>)}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="file"
          defaultValue=""
          render={({ field }) => (
            <FileUploader
              acceptableFiles=".xlsx,.pdf,.docx,.pptx"
              helpText="The file must be either .pdf, .pptx, .xlsx or .docx and must be less
      than 50 MB"
              label="Document"
              error={errors['file'] as never}
              disabled={isFormDisabled}
              required={!isFormDisabled}
              data-testid="file-uploader"
              {...field}
              value={field.value as File | undefined}
            />
          )}
        />
      </div>
      <Controller
        control={control}
        name="description"
        render={({ field }) => {
          const textareaProps: ComponentProps<typeof Textarea> = {
            placeholder: 'Please type file description...',
            label: 'File description',
            disabled: isFormDisabled,
            'data-testid': 'file-description-textarea',
            ...field,
            value: field.value as string,
          };

          return (
            <Textarea
              {...(textareaProps as unknown as ComponentProps<typeof Textarea>)}
            />
          );
        }}
      />
    </div>
  );
};

export default FilesDetailsForm;
